import { computed, onScopeDispose, watch, type Ref } from 'vue'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData
} from '@tanstack/vue-query'
import { ApiClient, type ApiRequestConfig } from '@/utils/api-client'
import { ENDPOINTS } from '@/api'
import { nextReviewPage, numeric, teacherLink } from './adapters'
import type {
  ReviewDraft,
  ReviewPage,
  ReviewSort,
  TeacherProfile,
  TeacherRatings,
  TeacherResponse,
  TeacherReview
} from './types'

const localErrors: ApiRequestConfig = {
  suppressGlobalErrorRedirects: true,
  retainUnauthorizedRedirect: true
}
export const getTeacherProfile = (id: string, signal?: AbortSignal) =>
  ApiClient.get<TeacherResponse<TeacherProfile>>(ENDPOINTS.Teachers.Profile(id), {
    ...localErrors,
    signal
  })
export const getTeacherRatings = (id: string, signal?: AbortSignal) =>
  ApiClient.get<TeacherResponse<TeacherRatings>>(ENDPOINTS.Teachers.Ratings(id), {
    ...localErrors,
    signal
  })
export const getTeacherReviews = (
  id: string,
  page: number,
  sort: ReviewSort,
  signal?: AbortSignal
) =>
  ApiClient.get<ReviewPage>(ENDPOINTS.Teachers.Reviews(id), {
    ...localErrors,
    signal,
    params: { page, orderBy: sort.orderBy, orderType: sort.orderType }
  })
export const createTeacherReview = (id: string, draft: ReviewDraft) =>
  ApiClient.post<TeacherResponse<unknown>>(
    ENDPOINTS.Teachers.Reviews(id),
    { score: draft.score, title: draft.title.trim(), comment: draft.comment.trim() },
    localErrors
  )
export async function toggleTeacherHelpful(commentId: string) {
  const response = await ApiClient.post<TeacherResponse<{ success: boolean }>>(
    ENDPOINTS.Comments.comment_reaction,
    { commentId, action: 1 },
    localErrors
  )
  if (!response.data?.success) throw new Error('Reaction rejected')
  return response
}

// Account is part of every key: profile courses contain viewer-specific enrollment.
export const teacherKeys = {
  context: (account: string, id: string) => ['teachers', account, id] as const,
  profile: (account: string, id: string) =>
    [...teacherKeys.context(account, id), 'profile'] as const,
  ratings: (account: string, id: string) =>
    [...teacherKeys.context(account, id), 'ratings'] as const,
  reviews: (account: string, id: string) =>
    [...teacherKeys.context(account, id), 'reviews'] as const
}
export function useTeacherQueries(
  id: string,
  account: string,
  sort: Ref<ReviewSort>
) {
  const queryClient = useQueryClient()
  const contextKey = teacherKeys.context(account, id)
  const enabled = Boolean(account && teacherLink(id))
  const profile = useQuery({
    queryKey: teacherKeys.profile(account, id),
    queryFn: ({ signal }) => getTeacherProfile(id, signal),
    enabled,
    retry: false,
    gcTime: 0
  })
  const ratings = useQuery({
    queryKey: teacherKeys.ratings(account, id),
    queryFn: ({ signal }) => getTeacherRatings(id, signal),
    enabled,
    retry: false,
    gcTime: 0
  })
  const reviews = useInfiniteQuery({
    queryKey: computed(() => [
      ...teacherKeys.reviews(account, id),
      sort.value.orderBy,
      sort.value.orderType
    ]),
    queryFn: ({ pageParam, signal, queryKey }) =>
      getTeacherReviews(
        id,
        pageParam,
        { label: '', orderBy: Number(queryKey[4]), orderType: Number(queryKey[5]) },
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => nextReviewPage(lastPage.meta),
    enabled,
    retry: false,
    gcTime: 0
  })
  const items = computed(() => {
    const unique = new Map<string, TeacherReview>()
    for (const page of reviews.data.value?.pages ?? [])
      for (const review of page.data ?? [])
        if (!unique.has(review.id)) unique.set(review.id, review)
    return [...unique.values()]
  })
  const create = useMutation({
    mutationFn: (draft: ReviewDraft) => createTeacherReview(id, draft),
    retry: false,
    onSuccess: () => {
      // Reset to page one after create, but never insert its different acknowledgement shape.
      queryClient.setQueriesData<InfiniteData<ReviewPage>>(
        { queryKey: teacherKeys.reviews(account, id) },
        (data) =>
          data
            ? {
                pages: data.pages.slice(0, 1),
                pageParams: data.pageParams.slice(0, 1)
              }
            : data
      )
      return queryClient.invalidateQueries({ queryKey: contextKey })
    }
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: contextKey })
  onScopeDispose(() => {
    void queryClient.cancelQueries({ queryKey: contextKey })
  })
  return { profile, ratings, reviews, items, create, refresh }
}

export function useTeacherHelpful(account: string, id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: toggleTeacherHelpful,
    retry: false,
    onSuccess: (_response, commentId) => {
      client.setQueriesData<InfiniteData<ReviewPage>>(
        { queryKey: teacherKeys.reviews(account, id) },
        (data) =>
          data
            ? {
                ...data,
                pages: data.pages.map((page) => ({
                  ...page,
                  data: page.data.map((review) =>
                    review.id !== commentId
                      ? review
                      : {
                          ...review,
                          likes: Math.max(
                            0,
                            numeric(review.likes) + (review.isLike ? -1 : 1)
                          ),
                          isLike: !review.isLike,
                          isDisLike: false,
                          disLikes: Math.max(
                            0,
                            numeric(review.disLikes) - (review.isDisLike ? 1 : 0)
                          )
                        }
                  )
                }))
              }
            : data
      )
      return client.invalidateQueries({ queryKey: teacherKeys.reviews(account, id) })
    }
  })
}

/** Mounted once in the app so signing out clears teacher caches on every route. */
export function useTeacherAccountCleanup(account: Ref<string>) {
  const client = useQueryClient()
  watch(
    account,
    (_current, previous) => {
      const filters = { queryKey: ['teachers', previous] }
      void client.cancelQueries(filters)
      client.removeQueries(filters)
    },
    { flush: 'sync' }
  )
}
